import React, { useMemo } from 'react';
import { Breadcrumbs, Link, Typography, makeStyles } from '@material-ui/core';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { ProjectFolder } from '@clef/shared/types';

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(6),
    '& .MuiBreadcrumbs-li': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  link: {
    cursor: 'pointer',
    color: theme.palette.greyModern[600],
    '&:hover': {
      textDecoration: 'underline',
      color: theme.palette.blue[600],
    },
  },
  current: {
    color: theme.palette.greyModern[900],
    fontWeight: 500,
  },
}));

interface FolderBreadcrumbProps {
  currentFolderId: number | null;
  folders: ProjectFolder[];
  onNavigate: (folderId: number | null) => void;
}

export const FolderBreadcrumb: React.FC<FolderBreadcrumbProps> = ({
  currentFolderId,
  folders,
  onNavigate,
}) => {
  const styles = useStyles();

  // Build the breadcrumb path from root to current folder
  const breadcrumbPath = useMemo(() => {
    if (currentFolderId === null) {
      return [];
    }

    const folderMap = new Map(folders.map(f => [f.id, f]));
    const path: ProjectFolder[] = [];
    let current = folderMap.get(currentFolderId);

    while (current) {
      path.unshift(current);
      current = current.parentFolderId ? folderMap.get(current.parentFolderId) : undefined;
    }

    return path;
  }, [currentFolderId, folders]);

  // Don't show breadcrumb at root level
  if (currentFolderId === null) {
    return null;
  }

  return (
    <Breadcrumbs
      separator={<NavigateNextIcon fontSize="small" />}
      aria-label="folder breadcrumb"
      className={styles.root}
    >
      <Link
        className={styles.link}
        onClick={() => onNavigate(null)}
        component="button"
        variant="body1"
      >
        {t('Projects')}
      </Link>
      {breadcrumbPath.map((folder, index) => {
        const isLast = index === breadcrumbPath.length - 1;
        if (isLast) {
          return (
            <Typography key={folder.id} variant="body1" className={styles.current}>
              {folder.name}
            </Typography>
          );
        }
        return (
          <Link
            key={folder.id}
            className={styles.link}
            onClick={() => onNavigate(folder.id)}
            component="button"
            variant="body1"
          >
            {folder.name}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
};

export default FolderBreadcrumb;
